import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Search, 
  SlidersHorizontal, 
  X, 
  Fuel, 
  Gauge, 
  Calendar,
  Car as CarIcon,
  ChevronDown,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

// Helper function to convert Google Drive share URL to direct image URL
const getDirectImageUrl = (url) => {
  if (!url) return null;
  
  // Handle Google Drive share links
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (driveMatch) {
    return `https://drive.google.com/uc?export=view&id=${driveMatch[1]}`;
  }
  
  // Handle Google Drive direct links that are already in uc format
  if (url.includes('drive.google.com/uc')) {
    return url;
  }
  
  // Return original URL for other valid URLs
  return url;
};

// Car Card Component
const CarCard = ({ car }) => {
  const isSold = car.status === 'sold';
  
  // Get the first image from the images array
  const mainImage = car.images?.[0] || '';
  
  // Only use the image if it's a valid URL, otherwise use placeholder
  const isValidUrl = mainImage && (mainImage.startsWith('http://') || mainImage.startsWith('https://'));
  const placeholderImage = 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&auto=format&fit=crop';
  
  // Convert Google Drive URLs to direct image URLs
  const imageUrl = isValidUrl ? getDirectImageUrl(mainImage) : placeholderImage;
  
  return (
    <Link to={`/cars/${car.id}`} data-testid={`car-card-link-${car.sheet_id}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="group relative overflow-hidden bg-[#0A0F14] border border-white/10 hover:border-[#48CCCD]/30 transition-all duration-500 cursor-pointer"
        data-testid={`car-card-${car.sheet_id}`}
      >
        {/* Image Container - 4:3 aspect ratio for iPhone landscape photos */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={imageUrl}
            alt={`${car.make} ${car.model}`}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onError={(e) => {
              e.target.src = placeholderImage;
            }}
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F14] via-transparent to-transparent" />
          
          {/* SOLD Badge */}
          {isSold && (
            <div 
              className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 text-xs font-bold uppercase tracking-wider"
              data-testid={`sold-badge-${car.sheet_id}`}
            >
              SOLD
            </div>
          )}
          
          {/* Featured Badge */}
          {car.featured && !isSold && (
            <div className="absolute top-4 left-4 bg-[#48CCCD] text-black px-3 py-1 text-xs font-bold uppercase tracking-wider">
              Featured
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-5">
          {/* Title Row */}
          <div className="mb-3">
            <h3 className="font-serif text-lg font-bold text-[#F8FAFC] leading-tight">
              {car.make} {car.model}
            </h3>
            {car.trim && (
              <p className="text-xs text-[#48CCCD] font-sans uppercase tracking-wider mt-1">
                {car.trim}
              </p>
            )}
          </div>
          
          {/* Price */}
          <div className="mb-4">
            <p className="font-serif text-2xl font-bold text-[#F8FAFC]">
              €{car.price.toLocaleString()}
            </p>
          </div>
          
          {/* Specs Grid */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="flex items-center gap-2 text-[#94A3B8]">
              <Calendar size={14} strokeWidth={1.5} className="text-[#48CCCD]" />
              <span className="text-xs font-sans">{car.year}</span>
            </div>
            <div className="flex items-center gap-2 text-[#94A3B8]">
              <Gauge size={14} strokeWidth={1.5} className="text-[#48CCCD]" />
              <span className="text-xs font-sans">{car.mileage.toLocaleString()} mi</span>
            </div>
            <div className="flex items-center gap-2 text-[#94A3B8]">
              <Fuel size={14} strokeWidth={1.5} className="text-[#48CCCD]" />
              <span className="text-xs font-sans">{car.fuel_type}</span>
            </div>
            <div className="flex items-center gap-2 text-[#94A3B8]">
              <CarIcon size={14} strokeWidth={1.5} className="text-[#48CCCD]" />
              <span className="text-xs font-sans">{car.transmission}</span>
            </div>
          </div>
          
          {/* Body Type Tag */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-sans px-2 py-1 bg-white/5 border border-white/10">
              {car.body_type}
            </span>
            {car.colour && (
              <span className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-sans px-2 py-1 bg-white/5 border border-white/10">
                {car.colour}
              </span>
            )}
          </div>
        </div>
        
        {/* Hover Effect Border */}
        <div className="absolute inset-0 border-2 border-[#48CCCD] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </motion.div>
    </Link>
  );
};

// Filters Component
const Filters = ({ filters, filterOptions, onFilterChange, onClearFilters }) => {
  const hasActiveFilters = Object.values(filters).some(v => v !== '' && v !== null);
  
  return (
    <div className="bg-[#0A0F14] border border-white/10 p-6" data-testid="filters-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={18} className="text-[#48CCCD]" />
          <h3 className="font-sans text-sm font-medium text-[#F8FAFC] uppercase tracking-wider">
            Filters
          </h3>
        </div>
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="text-xs text-[#48CCCD] hover:text-white transition-colors font-sans uppercase tracking-wider flex items-center gap-1"
            data-testid="clear-filters-btn"
          >
            <X size={14} />
            Clear
          </button>
        )}
      </div>
      
      <div className="space-y-5">
        {/* Make Filter */}
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
            Make
          </label>
          <Select
            value={filters.make || 'all'}
            onValueChange={(value) => onFilterChange('make', value === 'all' ? '' : value)}
          >
            <SelectTrigger 
              className="w-full bg-[#16202A] border-white/10 text-[#F8FAFC] h-10"
              data-testid="filter-make"
            >
              <SelectValue placeholder="All Makes" />
            </SelectTrigger>
            <SelectContent className="bg-[#16202A] border-white/10">
              <SelectItem value="all" className="text-[#F8FAFC]">All Makes</SelectItem>
              {filterOptions.makes?.map(make => (
                <SelectItem key={make} value={make} className="text-[#F8FAFC]">
                  {make}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Price Range */}
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
            Price Range
          </label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              placeholder="Min"
              value={filters.min_price || ''}
              onChange={(e) => onFilterChange('min_price', e.target.value)}
              className="bg-[#16202A] border-white/10 text-[#F8FAFC] h-10 text-sm"
              data-testid="filter-min-price"
            />
            <Input
              type="number"
              placeholder="Max"
              value={filters.max_price || ''}
              onChange={(e) => onFilterChange('max_price', e.target.value)}
              className="bg-[#16202A] border-white/10 text-[#F8FAFC] h-10 text-sm"
              data-testid="filter-max-price"
            />
          </div>
        </div>
        
        {/* Year Range */}
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
            Year
          </label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              placeholder="From"
              value={filters.year_from || ''}
              onChange={(e) => onFilterChange('year_from', e.target.value)}
              className="bg-[#16202A] border-white/10 text-[#F8FAFC] h-10 text-sm"
              data-testid="filter-year-from"
            />
            <Input
              type="number"
              placeholder="To"
              value={filters.year_to || ''}
              onChange={(e) => onFilterChange('year_to', e.target.value)}
              className="bg-[#16202A] border-white/10 text-[#F8FAFC] h-10 text-sm"
              data-testid="filter-year-to"
            />
          </div>
        </div>
        
        {/* Fuel Type */}
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
            Fuel Type
          </label>
          <Select
            value={filters.fuel_type || 'all'}
            onValueChange={(value) => onFilterChange('fuel_type', value === 'all' ? '' : value)}
          >
            <SelectTrigger 
              className="w-full bg-[#16202A] border-white/10 text-[#F8FAFC] h-10"
              data-testid="filter-fuel-type"
            >
              <SelectValue placeholder="All Fuel Types" />
            </SelectTrigger>
            <SelectContent className="bg-[#16202A] border-white/10">
              <SelectItem value="all" className="text-[#F8FAFC]">All Fuel Types</SelectItem>
              {filterOptions.fuel_types?.map(type => (
                <SelectItem key={type} value={type} className="text-[#F8FAFC]">
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Transmission */}
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
            Transmission
          </label>
          <Select
            value={filters.transmission || 'all'}
            onValueChange={(value) => onFilterChange('transmission', value === 'all' ? '' : value)}
          >
            <SelectTrigger 
              className="w-full bg-[#16202A] border-white/10 text-[#F8FAFC] h-10"
              data-testid="filter-transmission"
            >
              <SelectValue placeholder="All Transmissions" />
            </SelectTrigger>
            <SelectContent className="bg-[#16202A] border-white/10">
              <SelectItem value="all" className="text-[#F8FAFC]">All Transmissions</SelectItem>
              {filterOptions.transmissions?.map(trans => (
                <SelectItem key={trans} value={trans} className="text-[#F8FAFC]">
                  {trans}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

// Mobile Filters Toggle
const MobileFiltersToggle = ({ isOpen, onClick, activeCount }) => (
  <button
    onClick={onClick}
    className="lg:hidden flex items-center gap-2 bg-[#16202A] border border-white/10 px-4 py-3 text-[#F8FAFC]"
    data-testid="mobile-filters-toggle"
  >
    <SlidersHorizontal size={18} />
    <span className="text-sm font-sans">Filters</span>
    {activeCount > 0 && (
      <span className="bg-[#48CCCD] text-black text-xs px-2 py-0.5 font-bold">
        {activeCount}
      </span>
    )}
    <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
  </button>
);

// Main Cars Page Component
const CarsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const searchQuery = searchParams.get('q') || '';
  
  const [cars, setCars] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  
  const [filters, setFilters] = useState({
    make: '',
    min_price: '',
    max_price: '',
    year_from: '',
    year_to: '',
    fuel_type: '',
    transmission: ''
  });
  
  // Debounce filters for price/year inputs
  const debouncedFilters = useDebounce(filters, 500);
  
  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await fetch(`${API}/cars/filters`);
        const data = await response.json();
        setFilterOptions(data);
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);
  
  // Build URL params helper
  const buildParams = useCallback((pageNum) => {
    const params = new URLSearchParams();
    params.append('page', pageNum.toString());
    params.append('page_size', PAGE_SIZE.toString());
    
    // Add search query if present
    if (searchQuery) {
      params.append('q', searchQuery);
    }
    
    if (debouncedFilters.make) params.append('make', debouncedFilters.make);
    if (debouncedFilters.min_price) params.append('min_price', debouncedFilters.min_price);
    if (debouncedFilters.max_price) params.append('max_price', debouncedFilters.max_price);
    if (debouncedFilters.year_from) params.append('year_from', debouncedFilters.year_from);
    if (debouncedFilters.year_to) params.append('year_to', debouncedFilters.year_to);
    if (debouncedFilters.fuel_type) params.append('fuel_type', debouncedFilters.fuel_type);
    if (debouncedFilters.transmission) params.append('transmission', debouncedFilters.transmission);
    
    return params;
  }, [searchQuery, debouncedFilters]);
  
  // Fetch cars (initial load or filter change)
  useEffect(() => {
    const fetchCars = async () => {
      setLoading(true);
      setPage(1); // Reset to page 1 on filter/search change
      try {
        const params = buildParams(1);
        const url = `${API}/cars?${params.toString()}`;
        const response = await fetch(url);
        const data = await response.json();
        
        setCars(data.cars || []);
        setTotal(data.total || 0);
        setHasMore(data.has_more || false);
      } catch (error) {
        console.error('Failed to fetch cars:', error);
        setCars([]);
        setTotal(0);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCars();
  }, [debouncedFilters, searchQuery, buildParams]);
  
  // Load more cars
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    const nextPage = page + 1;
    
    try {
      const params = buildParams(nextPage);
      const url = `${API}/cars?${params.toString()}`;
      const response = await fetch(url);
      const data = await response.json();
      
      setCars(prev => [...prev, ...(data.cars || [])]);
      setPage(nextPage);
      setHasMore(data.has_more || false);
    } catch (error) {
      console.error('Failed to load more cars:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [page, hasMore, loadingMore, buildParams]);
  
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);
  
  const handleClearFilters = useCallback(() => {
    setFilters({
      make: '',
      min_price: '',
      max_price: '',
      year_from: '',
      year_to: '',
      fuel_type: '',
      transmission: ''
    });
  }, []);
  
  const handleClearSearch = useCallback(() => {
    // Navigate to /cars without the q parameter, preserving other filters
    const params = new URLSearchParams();
    if (filters.make) params.append('make', filters.make);
    if (filters.min_price) params.append('min_price', filters.min_price);
    if (filters.max_price) params.append('max_price', filters.max_price);
    if (filters.year_from) params.append('year_from', filters.year_from);
    if (filters.year_to) params.append('year_to', filters.year_to);
    if (filters.fuel_type) params.append('fuel_type', filters.fuel_type);
    if (filters.transmission) params.append('transmission', filters.transmission);
    
    navigate(`/cars${params.toString() ? `?${params.toString()}` : ''}`);
  }, [navigate, filters]);
  
  const activeFilterCount = Object.values(filters).filter(v => v !== '' && v !== null).length;
  
  return (
    <div className="min-h-screen bg-[#020405]" data-testid="cars-page">
      {/* Hero Header */}
      <div className="bg-[#0A0F14] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#48CCCD] font-sans text-xs uppercase tracking-[0.3em] mb-4">
              Our Inventory
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F8FAFC] tracking-tight mb-4">
              {searchQuery ? 'Search Results' : 'Available Vehicles'}
            </h1>
            <p className="font-sans text-[#94A3B8] text-base max-w-xl">
              {searchQuery 
                ? `Showing results for your search. Use filters to narrow down further.`
                : 'Browse our carefully curated selection of premium pre-owned vehicles. Each car has been thoroughly inspected to meet our high standards.'
              }
            </p>
          </motion.div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Search Results Label */}
        {searchQuery && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 flex items-center gap-3 flex-wrap"
            data-testid="search-results-label"
          >
            <div className="flex items-center gap-2 bg-[#16202A] border border-white/10 px-4 py-2">
              <Search size={16} className="text-[#48CCCD]" />
              <span className="text-sm text-[#F8FAFC] font-sans">
                Search results for "<span className="text-[#48CCCD]">{searchQuery}</span>"
              </span>
              <button
                onClick={handleClearSearch}
                className="ml-2 text-[#94A3B8] hover:text-red-400 transition-colors"
                data-testid="clear-search-btn"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}
        
        {/* Results Count & Mobile Filter Toggle */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-[#94A3B8] font-sans">
            <span className="text-[#F8FAFC] font-medium">{total}</span> vehicle{total !== 1 ? 's' : ''} found
          </p>
          <MobileFiltersToggle 
            isOpen={mobileFiltersOpen}
            onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
            activeCount={activeFilterCount}
          />
        </div>
        
        {/* Mobile Filters Panel */}
        {mobileFiltersOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden mb-6"
          >
            <Filters
              filters={filters}
              filterOptions={filterOptions}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />
          </motion.div>
        )}
        
        <div className="flex gap-8">
          {/* Desktop Filters Sidebar */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24">
              <Filters
                filters={filters}
                filterOptions={filterOptions}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />
            </div>
          </div>
          
          {/* Cars Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-[#0A0F14] border border-white/10 animate-pulse">
                    <div className="aspect-[4/3] bg-[#16202A]" />
                    <div className="p-5 space-y-3">
                      <div className="h-5 w-3/4 bg-[#16202A]" />
                      <div className="h-8 w-1/2 bg-[#16202A]" />
                      <div className="h-4 w-full bg-[#16202A]" />
                    </div>
                  </div>
                ))}
              </div>
            ) : cars.length > 0 ? (
              <>
                <div 
                  className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                  data-testid="cars-grid"
                >
                  {cars.map((car, index) => (
                    <motion.div
                      key={car.sheet_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.5) }}
                    >
                      <CarCard car={car} />
                    </motion.div>
                  ))}
                </div>
                
                {/* Load More Button */}
                {hasMore && (
                  <div className="flex justify-center mt-12">
                    <Button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="h-12 px-8 bg-[#16202A] border border-white/20 text-[#F8FAFC] hover:bg-[#48CCCD] hover:text-black hover:border-[#48CCCD] font-sans text-sm uppercase tracking-wider transition-all duration-300"
                      data-testid="load-more-btn"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 size={18} className="mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          Load More
                          <span className="ml-2 text-xs text-[#94A3B8]">
                            ({cars.length} of {total})
                          </span>
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                {/* All loaded indicator */}
                {!hasMore && cars.length > 0 && cars.length < total && (
                  <p className="text-center text-[#94A3B8] text-sm font-sans mt-8">
                    Showing all {cars.length} vehicles
                  </p>
                )}
              </>
            ) : (
              <div className="text-center py-20" data-testid="no-cars-message">
                <CarIcon size={48} className="mx-auto text-[#94A3B8] mb-4" strokeWidth={1} />
                <h3 className="font-serif text-xl text-[#F8FAFC] mb-2">
                  {searchQuery ? 'No cars found matching your search' : 'No vehicles found'}
                </h3>
                <p className="text-[#94A3B8] font-sans text-sm mb-6">
                  {searchQuery 
                    ? `We couldn't find any cars matching "${searchQuery}". Try a different search or browse all vehicles.`
                    : 'Try adjusting your filters to see more results.'
                  }
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {searchQuery && (
                    <Button
                      onClick={handleClearSearch}
                      className="bg-[#48CCCD] text-black hover:bg-white"
                      data-testid="clear-search-empty-btn"
                    >
                      Clear Search
                    </Button>
                  )}
                  {activeFilterCount > 0 && (
                    <Button
                      onClick={handleClearFilters}
                      variant={searchQuery ? "outline" : "default"}
                      className={searchQuery ? "border-white/20 text-white hover:bg-white/10" : "bg-[#48CCCD] text-black hover:bg-white"}
                      data-testid="clear-filters-empty-btn"
                    >
                      Clear Filters
                    </Button>
                  )}
                  {!searchQuery && activeFilterCount === 0 && (
                    <Link to="/">
                      <Button className="bg-[#48CCCD] text-black hover:bg-white">
                        Return Home
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarsPage;
